import { useState } from "react";
import type { DartThrow } from "@shared/index";

const NUMBERS = [
  1, 2, 3, 4, 5,
  6, 7, 8, 9, 10,
  11, 12, 13, 14, 15,
  16, 17, 18, 19, 20,
];

interface Props {
  disabled?: boolean;
  onDart: (dart: DartThrow) => void;
  onUndo: () => void;
  onConfirm: () => void;
  canUndo: boolean;
  canConfirm: boolean;
  /** When set, only S/D/T of these cricket numbers (plus Miss). */
  cricketTargets?: number[];
}

export default function DartPad({
  disabled,
  onDart,
  onUndo,
  onConfirm,
  canUndo,
  canConfirm,
  cricketTargets,
}: Props) {
  const [mod, setMod] = useState<1 | 2 | 3>(1);

  function send(segment: number, multiplier: 1 | 2 | 3) {
    onDart({ segment, multiplier });
    setMod(1);
  }

  if (cricketTargets && cricketTargets.length > 0) {
    const numbers = cricketTargets.filter((n) => n >= 1 && n <= 20);
    const includeBull = cricketTargets.includes(25);
    return (
      <div className="cricket-pad flex min-h-0 flex-col gap-1.5">
        <div className="grid min-h-0 grid-cols-[minmax(2.6rem,0.7fr)_repeat(3,minmax(0,1fr))] gap-1.5">
          <div className="text-[10px] font-black uppercase tracking-widest text-slate-500" />
          <div className="text-center text-[10px] font-black uppercase tracking-widest text-slate-400">S</div>
          <div className="text-center text-[10px] font-black uppercase tracking-widest text-amber-glow">D</div>
          <div className="text-center text-[10px] font-black uppercase tracking-widest text-crimson">T</div>
          {numbers.map((n) => (
            <CricketNumberRow key={n} n={n} disabled={disabled} onSend={send} />
          ))}
          {includeBull ? <BullRow disabled={disabled} onSend={send} /> : null}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <button className="pad-btn bg-slate-200 text-ink-950" onClick={() => send(0, 1)} disabled={disabled}>
            Daneben
          </button>
          <button
            className="pad-btn bg-ink-800 text-slate-200 disabled:opacity-40"
            onClick={onUndo}
            disabled={disabled || !canUndo}
          >
            Rückgängig
          </button>
          <button
            className="pad-btn bg-emerald-500 text-ink-950 disabled:opacity-40"
            onClick={onConfirm}
            disabled={disabled || !canConfirm}
          >
            Fertig
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-3 gap-2">
        <button
          className={`pad-btn ${mod === 1 ? "bg-ink-600 text-white" : "bg-ink-800 text-slate-300"}`}
          onClick={() => setMod(1)}
          disabled={disabled}
        >
          Single
        </button>
        <button
          className={`pad-btn ${mod === 2 ? "bg-amber-glow text-ink-950" : "bg-ink-800 text-amber-glow"}`}
          onClick={() => setMod(2)}
          disabled={disabled}
        >
          Double
        </button>
        <button
          className={`pad-btn ${mod === 3 ? "bg-crimson text-white" : "bg-ink-800 text-crimson"}`}
          onClick={() => setMod(3)}
          disabled={disabled}
        >
          Triple
        </button>
      </div>

      <div className="grid grid-cols-5 gap-2">
        {NUMBERS.map((n) => (
          <button
            key={n}
            className="pad-btn bg-ink-700 text-white active:bg-ink-600"
            onClick={() => send(n, mod)}
            disabled={disabled}
          >
            {mod === 2 ? `D${n}` : mod === 3 ? `T${n}` : n}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          className="pad-btn bg-amber-glow/90 text-ink-950 text-lg"
          onClick={() => send(25, mod === 3 ? 2 : (mod as 1 | 2))}
          disabled={disabled}
        >
          {mod === 2 ? "DBull" : "Bull"}
        </button>
        <button
          className="pad-btn bg-slate-200 text-ink-950 text-lg"
          onClick={() => send(0, 1)}
          disabled={disabled}
        >
          Daneben
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          className="pad-btn bg-ink-800 text-slate-200 disabled:opacity-40"
          onClick={onUndo}
          disabled={disabled || !canUndo}
        >
          Rückgängig
        </button>
        <button
          className="pad-btn bg-emerald-500 text-ink-950 disabled:opacity-40"
          onClick={onConfirm}
          disabled={disabled || !canConfirm}
        >
          Visit bestätigen
        </button>
      </div>
    </div>
  );
}

function CricketNumberRow({
  n,
  disabled,
  onSend,
}: {
  n: number;
  disabled?: boolean;
  onSend: (segment: number, multiplier: 1 | 2 | 3) => void;
}) {
  return (
    <>
      <div className="flex items-center justify-center font-display text-xl text-white sm:text-2xl">{n}</div>
      <button className="pad-btn bg-ink-700 text-white" disabled={disabled} onClick={() => onSend(n, 1)}>
        S{n}
      </button>
      <button className="pad-btn bg-amber-glow/90 text-ink-950" disabled={disabled} onClick={() => onSend(n, 2)}>
        D{n}
      </button>
      <button className="pad-btn bg-crimson text-white" disabled={disabled} onClick={() => onSend(n, 3)}>
        T{n}
      </button>
    </>
  );
}

function BullRow({
  disabled,
  onSend,
}: {
  disabled?: boolean;
  onSend: (segment: number, multiplier: 1 | 2 | 3) => void;
}) {
  return (
    <>
      <div className="flex items-center justify-center font-display text-xl text-white sm:text-2xl">Bull</div>
      <button className="pad-btn bg-ink-700 text-white" disabled={disabled} onClick={() => onSend(25, 1)}>
        25
      </button>
      <button className="pad-btn bg-amber-glow/90 text-ink-950" disabled={disabled} onClick={() => onSend(25, 2)}>
        50
      </button>
      <div />
    </>
  );
}
